import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Box } from '@legion-ui/core';
import Button from '@/components/Button';
import EmailEditor from '@/components/EmailEditor';
import { useEmailBlastById, useUpdateEmailBlast } from '@/hooks/useEmailBlastQuery';
import { notify } from '@/services/notify';
import useLoadingStore from '@/store/loading';
import { ToastContainer } from 'react-toastify';
import LoadingOverlay from '@/components/LoadingOverlay';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const EditEmailBlast: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Fetch email blast by ID
  const { data: emailDraft, isLoading } = useEmailBlastById(id!);
  
  // Update mutation
  const updateEmailBlastMutation = useUpdateEmailBlast();
  const { setLoading } = useLoadingStore();

  // State for editing
  const [subject, setSubject] = useState('');
  const [bodyEmail, setBodyEmail] = useState('');

  // Update state when data is loaded
  useEffect(() => {
    if (emailDraft) {
      setSubject(emailDraft.subject);
      setBodyEmail(emailDraft.content);
    }
  }, [emailDraft]);

  const handleSaveAsDraft = () => {
    if (!subject.trim() || !bodyEmail.trim()) {
      notify('Please fill in both Subject and Body Email fields before saving.', { 
        type: 'error' 
      });
      return;
    }

    // Check if email blast is still in draft status
    if (emailDraft?.status !== 'draft') {
      notify('Only draft emails can be edited.', { type: 'error' });
      return;
    }

    setLoading(true); // Show loading overlay

    updateEmailBlastMutation.mutate(
      {
        id: id!,
        data: {
          subject: subject.trim(),
          content: bodyEmail.trim(),
          recipients: emailDraft.recipients.map(r => r.username), // Extract usernames only
        },
      },
      {
        onSuccess: () => {
          notify('Draft saved successfully!', { type: 'success' });
          
          // Delay navigation to allow toast to be visible
          setTimeout(() => {
            setLoading(false); // Hide loading overlay
            navigate('/email-blast', { replace: true });
          }, 2000);
        },
        onError: (error) => {
          setLoading(false); // Hide loading overlay on error
          console.error('Failed to update email blast:', error);
          notify('Failed to save draft. Please try again.', { type: 'error' });
        },
      }
    );
  };

  const handleAddRecipient = () => {
    if (!subject.trim() || !bodyEmail.trim()) {
      notify('Please fill in both Subject and Body Email fields before adding recipients.', {
        type: 'error'
      });
      return;
    }

    setLoading(true); // Show loading overlay

    // Save draft first
    updateEmailBlastMutation.mutate(
      {
        id: id!,
        data: {
          subject: subject.trim(),
          content: bodyEmail.trim(),
          recipients: emailDraft?.recipients?.map(r => r.username) || [],
        },
      },
      {
        onSuccess: () => {
          setLoading(false); // Hide loading overlay
          
          // Navigate to add recipients page
          navigate('/email-blast/add-recipients', {
            state: {
              emailBlastId: id,
              subject: subject.trim(),
              content: bodyEmail.trim(),
              existingRecipients: emailDraft?.recipients || [],
            }
          });
        },
        onError: (error) => {
          setLoading(false); // Hide loading overlay on error
          console.error('Failed to save draft:', error);
          notify('Failed to save draft. Please try again.', { type: 'error' });
        },
      }
    );
  };

  const handleCancel = () => {
    navigate('/email-blast');
  };

  if (isLoading) {
    return (
      <Container>
        <Box className="bg-white min-h-screen">
          <Box className="border-b border-gray-200 px-6 py-4">
            <Skeleton height={32} width={200} />
          </Box>
          <Box className="px-6 py-6">
            <Skeleton count={10} />
          </Box>
        </Box>
      </Container>
    );
  }

  if (!emailDraft) {
    return (
      <Container>
        <Box className="bg-white min-h-screen">
          <Box className="border-b border-gray-200 px-6 py-4">
            <h1 className="text-2xl font-bold text-gray-900">Email not found</h1>
          </Box>
          <Box className="px-6 py-6">
            <Button onClick={handleCancel} variant="outline">
              Back
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box className="bg-white min-h-screen">
        {/* Header */}
        <Box className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Edit Bulk Email</h1>
          <p className="text-sm text-gray-600 mt-1">Manage and edit email notification</p>
        </Box>

        {/* Content */}
        <Box className="px-6 py-6">
          {/* Compose Email Section */}
          <Box className="bg-gray-50 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Compose Email</h2>

            {/* Subject Input */}
            <Box className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => {
                  if (e.target.value.length <= 60) {
                    setSubject(e.target.value);
                  }
                }}
                placeholder="Example : Maintenance Developer portal"
                maxLength={60}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                {subject.length}/60 characters
              </p>
            </Box>

            {/* Body Email Editor */}
            <div className="mb-6 pb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Body Email <span className="text-red-500">*</span>
              </label>
              <EmailEditor value={bodyEmail} onChange={setBodyEmail} />
            </div>
          </Box>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-end gap-4 mt-8 pt-4">
            <Button
              variant="outline"
              size="md"
              onClick={handleCancel}
              className="px-6"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleSaveAsDraft}
              className="px-6"
            >
              Save as Draft
            </Button>
            <Button
              variant="solid"
              color="primary"
              size="md"
              onClick={handleAddRecipient}
              className="px-6"
            >
              Add Recipient
            </Button>
          </div>
        </Box>
      </Box>
      <LoadingOverlay />
      <ToastContainer />
    </Container>
  );
};

export default EditEmailBlast;
